import { IIntegrationSetup } from "api/interfaces";
import CompanyService from "api/services/CompanyService";
import { makeAutoObservable } from "mobx";
import { lang } from "../i18nLang";
import { CompanyStore, ContractIntegrationStore, MessageStore } from "./";

class Store {
  public readonly validations = {
    host: validationURL,
    user: validationData("user"),
    password: validationData("password"),
  };

  constructor() {
    makeAutoObservable(this);
  }

  async updateIntegrationSetup(updatedData: IIntegrationSetup) {
    this.resetAuthenticationError();
    const company = CompanyStore.currentCompany;
    if (!company) return false;

    try {
      const authenticated = await ContractIntegrationStore.authenticateUser(
        updatedData
      ).catch((error) => {
        this.dataWithAuthError = { ...updatedData };
        this.authenticationError = error.name;
        return false;
      });

      if (authenticated || !updatedData.host) {
        await CompanyService.updateIntegrationSetup(company.uid, updatedData);
        MessageStore.success(lang("integrationSetup.success.updated"));
      }

      return authenticated;
    } catch (error: any) {
      console.error(error);
      MessageStore.error(error.message);
      return false;
    }
  }

  private dataWithAuthError: any = undefined;
  private authenticationError: string = "";

  validateAuthError(data) {
    const error = IntegrationSetupStore.authenticationError;
    if (!error) return;

    const validations = {
      NoAccessPermissionException: {
        fields: ["user", "password"],
        type: "userPermission",
        isLink: true,
      },
      UserBlockedException: {
        fields: ["user", "password"],
        type: "userBlock",
        isLink: true,
      },
      UserNotFoundException: {
        fields: ["user", "password"],
        type: "userAndPass",
        isLink: false,
      },
      NetworkErrorException: {
        fields: ["host"],
        type: "integration",
        isLink: true,
      },
      InvalidTenantIdException: {
        fields: ["tenantId"],
        type: "invalidTenantId",
        isLink: false,
      },
    };

    const validation = validations[error];
    if (validation) {
      const field = validation.fields[0];
      if (this.dataWithAuthError[field] !== data[field]) {
        this.resetAuthenticationError();
      }
      return validation;
    }
    return;
  }

  resetAuthenticationError() {
    this.authenticationError = "";
    this.dataWithAuthError = undefined;
  }
}

function validationData(key) {
  return (data) => {
    if (allDataIsEmpty(data)) return;
    if (data[key] && data[key].trim() !== "") return;

    return {
      valid: false,
      text: lang("exceptions.requiredParam", "").trim(),
    };
  };
}

function allDataIsEmpty(data) {
  if (!data) return true;
  return ["password", "host", "user"].every(
    (key) => !data[key] || data[key] === ""
  );
}

function validationURL(data) {
  const error = validationData("host")(data);
  if (error) return error;

  const value = data.host;
  if (!value || value === "") return;

  try {
    new URL(value);
    return;
  } catch (_) {
    return {
      valid: false,
      text: lang("integrationSetup.error.integration"),
    };
  }
}

const IntegrationSetupStore = new Store();
export default IntegrationSetupStore;
